import { useState } from 'react';
import { Check, Clock } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type VoteType = 'maintenance' | 'disposal' | 'grace_disclosure' | 'refinance';

export interface DAOVote {
  id: string;
  vehicleId: string;
  vehicleDisplay: string;  // "2021 Toyota Camry"
  voteType: VoteType;
  title: string;
  description: string;
  proposedBy: string;      // first name + last initial only
  daysRemaining: number;
  votesFor: number;
  votesAgainst: number;
  totalVoters: number;
  userVote: 'for' | 'against' | null;  // null if not voted
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const VOTE_TYPE_CONFIG: Record<VoteType, { label: string; bg: string; text: string; border: string }> = {
  maintenance: {
    label: 'Maintenance',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  disposal: {
    label: 'Disposal',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
  },
  grace_disclosure: {
    label: 'Grace Disclosure',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  refinance: {
    label: 'Refinance',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
  },
};

// ---------------------------------------------------------------------------
// VoteCard
// ---------------------------------------------------------------------------

interface VoteCardProps {
  vote: DAOVote;
  onVote: (id: string, choice: 'for' | 'against') => void;
}

export function VoteCard({ vote, onVote }: VoteCardProps) {
  const [expanded, setExpanded] = useState(false);

  const typeCfg = VOTE_TYPE_CONFIG[vote.voteType];
  const notVotedCount = vote.totalVoters - vote.votesFor - vote.votesAgainst;
  const forPct = vote.totalVoters > 0 ? (vote.votesFor / vote.totalVoters) * 100 : 0;
  const againstPct = vote.totalVoters > 0 ? (vote.votesAgainst / vote.totalVoters) * 100 : 0;

  const urgentDays = vote.daysRemaining <= 2;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4 hover:shadow-md transition-shadow">
      {/* ------------------------------------------------------------------ */}
      {/* Top row: type badge + days remaining chip                           */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${typeCfg.bg} ${typeCfg.text} ${typeCfg.border}`}
        >
          {typeCfg.label}
        </span>

        <span
          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
            urgentDays
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`}
        >
          <Clock className="w-3 h-3" />
          {vote.daysRemaining}d left
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Vehicle + proposer                                                  */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <p className="text-xs text-gray-400 font-medium">
          {vote.vehicleDisplay} &middot; Proposed by {vote.proposedBy}
        </p>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Title + description                                                 */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-1">{vote.title}</h3>
        <p
          className={`text-sm text-gray-500 leading-relaxed ${
            expanded ? '' : 'line-clamp-3'
          }`}
        >
          {vote.description}
        </p>
        {vote.description.length > 160 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-xs font-semibold text-indigo-500 hover:text-indigo-700 transition-colors"
          >
            {expanded ? 'show less' : 'read more'}
          </button>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Grace disclosure warning callout                                    */}
      {/* ------------------------------------------------------------------ */}
      {vote.voteType === 'grace_disclosure' && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <span className="text-amber-500 text-base leading-none mt-0.5">&#9888;</span>
          <p className="text-xs text-amber-800 leading-snug">
            This vote would reveal which supporters chose to give as gifts rather than investments.
            This cannot be undone.
          </p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Progress bar                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
          {forPct > 0 && (
            <div
              className="bg-emerald-500 transition-all duration-300"
              style={{ width: `${forPct}%` }}
            />
          )}
          {againstPct > 0 && (
            <div
              className="bg-rose-500 transition-all duration-300"
              style={{ width: `${againstPct}%` }}
            />
          )}
        </div>
        <p className="mt-1.5 text-xs text-gray-400">
          <span className="text-emerald-600 font-semibold">{vote.votesFor} for</span>
          {' · '}
          <span className="text-rose-500 font-semibold">{vote.votesAgainst} against</span>
          {' · '}
          {notVotedCount} haven&apos;t voted
        </p>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Voting controls                                                     */}
      {/* ------------------------------------------------------------------ */}
      {vote.userVote === null ? (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onVote(vote.id, 'for')}
            className="flex-1 py-2 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
          >
            Vote For
          </button>
          <button
            onClick={() => onVote(vote.id, 'against')}
            className="flex-1 py-2 rounded-xl text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white transition-colors"
          >
            Vote Against
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-1 bg-gray-50 rounded-xl px-4 py-2.5">
          <Check className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <p className="text-sm font-semibold text-gray-600">
            You voted{' '}
            <span
              className={
                vote.userVote === 'for' ? 'text-emerald-600' : 'text-rose-500'
              }
            >
              {vote.userVote === 'for' ? 'For' : 'Against'}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
